import React from "react"
import { Routes, Route, Navigate } from "react-router-dom"
import { ToastProvider } from "@/components/ui/ToastProvider"
import { ModalProvider } from "@/components/modals/ModalProvider"
import { AuthProvider } from "@/components/layout/AuthProvider"
import { AppShell } from "@/components/layout/AppShell"
import { RequireAuth } from "@/components/auth/RequireAuth"

// Auth pages
import LoginPage from "@/pages/auth/LoginPage"
import RegisterPage from "@/pages/auth/RegisterPage"
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage"
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage"
import AuthCallbackPage from "@/pages/auth/AuthCallbackPage"

// Public pages (guests can view)
import FeedPage from "@/pages/FeedPage"
import TrendingPage from "@/pages/TrendingPage"
import SearchPage from "@/pages/SearchPage"
import TopicsPage from "@/pages/TopicsPage"
import TopicDetailPage from "@/pages/TopicDetailPage"
import ProfilePage from "@/pages/ProfilePage"
import PostDetailPage from "@/pages/PostDetailPage"

// Protected pages (must be signed in)
import CreatePostPage from "@/pages/CreatePostPage"
import EditPostPage from "@/pages/EditPostPage"
import NotificationsPage from "@/pages/NotificationsPage"
import MessagesPage from "@/pages/MessagesPage"
import BookmarksPage from "@/pages/BookmarksPage"
import FollowingPage from "@/pages/FollowingPage"
import DashboardPage from "@/pages/DashboardPage"
import SettingsPage from "@/pages/SettingsPage"
import AdminPage from "@/pages/admin/AdminPage"
import AdminUsersPage from "@/pages/admin/AdminUsersPage"
import AdminTopicsPage from "@/pages/admin/AdminTopicsPage"
import AdminModerationPage from "@/pages/admin/AdminModerationPage"
import AdminAnalyticsPage from "@/pages/admin/AdminAnalyticsPage"

function PoliciesRedirect() {
  React.useEffect(() => { window.location.href = "https://policies.squids.co.za" }, [])
  return null
}

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <ModalProvider>
          <Routes>
            {/* Auth routes */}
            <Route path="/auth/login" element={<LoginPage />} />
            <Route path="/auth/register" element={<RegisterPage />} />
            <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />

            {/* Policy redirects */}
            <Route path="/terms" element={<PoliciesRedirect />} />
            <Route path="/privacy" element={<PoliciesRedirect />} />
            <Route path="/help" element={<PoliciesRedirect />} />
            <Route path="/about" element={<PoliciesRedirect />} />

            {/* App shell wraps all app routes */}
            <Route element={<AppShell />}>
              <Route path="/" element={<Navigate to="/feed" replace />} />

              {/* Public (guests + signed-in) */}
              <Route path="/feed" element={<FeedPage />} />
              <Route path="/trending" element={<TrendingPage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/topics" element={<TopicsPage />} />
              <Route path="/topics/:slug" element={<TopicDetailPage />} />
              <Route path="/profile/:username" element={<ProfilePage />} />
              <Route path="/post/:id/:slug?" element={<PostDetailPage />} />

              {/* Protected (signed-in only) */}
              <Route path="/post/create" element={<RequireAuth><CreatePostPage /></RequireAuth>} />
              <Route path="/post/edit/:id" element={<RequireAuth><EditPostPage /></RequireAuth>} />
              <Route path="/notifications" element={<RequireAuth><NotificationsPage /></RequireAuth>} />
              <Route path="/messages" element={<RequireAuth><MessagesPage /></RequireAuth>} />
              <Route path="/bookmarks" element={<RequireAuth><BookmarksPage /></RequireAuth>} />
              <Route path="/following" element={<RequireAuth><FollowingPage /></RequireAuth>} />
              <Route path="/dashboard" element={<RequireAuth><DashboardPage /></RequireAuth>} />
              <Route path="/settings" element={<RequireAuth><SettingsPage /></RequireAuth>} />

              {/* Admin (signed-in only, admin check done inside) */}
              <Route path="/admin" element={<RequireAuth><AdminPage /></RequireAuth>} />
              <Route path="/admin/users" element={<RequireAuth><AdminUsersPage /></RequireAuth>} />
              <Route path="/admin/topics" element={<RequireAuth><AdminTopicsPage /></RequireAuth>} />
              <Route path="/admin/moderation" element={<RequireAuth><AdminModerationPage /></RequireAuth>} />
              <Route path="/admin/analytics" element={<RequireAuth><AdminAnalyticsPage /></RequireAuth>} />
            </Route>

            <Route path="*" element={<Navigate to="/feed" replace />} />
          </Routes>
        </ModalProvider>
      </ToastProvider>
    </AuthProvider>
  )
}
